import { FieldArray, FormikHelpers, FormikValues } from 'formik';
import React from 'react';
import { array, mixed, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, {
    Campo,
    Botao as BotaoForm,
    CaixaDeTexto,
    Arquivo,
    Contexto,
    CaixaDeSelecao
} from 'Component/Form';
import Modal from 'Component/Modal';
import Alternativa from 'Interface/Alternativas';
import Questao from 'Interface/Questoes';
import { Erro } from 'Interface/Requisicao';
import {
    useAdicionarAlternativaMutation,
    useEditarAlternativaMutation,
    useEditarQuestaoMutation,
    useRecuperarQuestaoQuery,
    useRemoverAlternativaMutation,
} from 'Service';
import { IMAGENS } from 'View/constants';

interface Props {
    exibir: boolean;
    id: number;
    aoFechar: (estado: boolean) => void;
}

const regras = object({
    enunciado: string()
        .nullable()
        .test('correta', 'Selecione a resposta correta', (valor, contexto) => {
            let selecionou = false;

            contexto.parent.alternativas.map(
                (alternativa: Alternativa) => alternativa.correta ? selecionou = true : null
            );

            return selecionou;
        })
        .test('correta', 'Existe mais de uma resposta correta', (valor, contexto) => {
            let corretas = 0;

            contexto.parent.alternativas.map(
                (alternativa: Alternativa) => alternativa.correta ? corretas++ : null
            );

            return corretas <= 1;
        })
        .required('Obrigatório'),
    imagem: mixed()
        .test('tamanho', 'O arquivo é muito grande', valor => {
            if (valor && valor.size) {
                return valor.size <= 500000;
            }
            return true;
        })
        .test('tipo', 'Formato não suportado', valor => {
            if (valor && valor.type) {
                return IMAGENS.includes(valor.type)
            }
            return true;
        })
        .optional(),
    alternativas: array().of(object().shape({
        texto: string().required('Obrigatório'),
    })),
});

const Editar = ({ exibir, aoFechar, id }: Props): React.ReactElement =>
{
    const toast = useToast();
    const [ editarQuestao, { isLoading: editandoQuestao } ] = useEditarQuestaoMutation();
    const [ adicionarAlternativa, { isLoading: adicionandoAlternativa } ] = useAdicionarAlternativaMutation();
    const [ editarAlternativa, { isLoading: editandoAlternativa } ] = useEditarAlternativaMutation();
    const [ removerAlternativa, { isLoading: removendoAlternativa } ] = useRemoverAlternativaMutation();

    const { data: { questao } = {} } = useRecuperarQuestaoQuery({
        id,
        contain: ['Alternativas'],
    });

    const editar = async (dados: Questao, form: FormikHelpers<FormikValues>): Promise<void> =>
    {
        try {
            const { alternativas, ...resto } = dados;

            await editarQuestao(resto).unwrap();

            if (alternativas) {
                await Promise.all(
                    alternativas?.map(alternativa => (
                        alternativa.id
                            ? editarAlternativa(alternativa)
                            : adicionarAlternativa(alternativa)
                    ))
                )
            }

            toast({ conteudo: 'Editado com sucesso!', tema: 'success' });

            aoFechar(false);
        } catch (erro) {
            const { data: errors } = erro as Erro;

            form.setStatus(errors);

            toast({
                titulo: 'Erro ao adicionar',
                conteudo: 'Verifique os logs para maiores detalhes.',
                tema: 'danger',
            });
        }
    }

    const remover = async (alternativa: Alternativa, callback: () => void): Promise<void> =>
    {
        try {
            await removerAlternativa(alternativa).unwrap();

            callback();
        } catch (erro) {
            toast({
                titulo: 'Erro ao remover',
                conteudo: 'Verifique os logs para maiores detalhes.',
                tema: 'danger',
            });
        }
    }

    return (
        <Modal lg exibir={ exibir } aoFechar={ aoFechar }>
            <Form
                onSubmit={ editar }
                regras={ regras }
                inicial={ questao }
                desabilitado={ editandoQuestao || adicionandoAlternativa || editandoAlternativa || removendoAlternativa }
            >
                <div className="row">
                    <div className="col-md-5 d-flex flex-column justify-content-between">
                        <div>
                            <CaixaDeTexto name="enunciado" rotulo="Enunciado da questão" />
                            <Arquivo name="imagem" ajuda="Caso tenha uma imagem, faça o upload aqui." />
                        </div>
                        <div className="mt-5">
                            <BotaoForm>
                                Salvar
                            </BotaoForm>
                            <Botao ms onClick={ () => aoFechar(false) } tema="light">
                                Cancelar
                            </Botao>
                        </div>
                    </div>
                    <div className="col-md-7 border-start my-n3 py-3">
                        <FieldArray name="alternativas">
                            { ({ push, remove }) => (
                                <React.Fragment>
                                    <Contexto>
                                        { ({ values }) => (
                                            <React.Fragment>
                                                { values.alternativas?.map((alternativa: Alternativa, indice: number) => (
                                                    <div key={ indice.toString() } className="hstack gap-3 mb-3">
                                                        <CaixaDeSelecao
                                                            name={ `alternativas.${indice}.correta` }
                                                            semMargem
                                                        />
                                                        <Campo
                                                            name={ `alternativas.${indice}.texto` }
                                                            rotulo={ `Alternativa #${indice + 1}` }
                                                            semMargem
                                                        />
                                                        <Botao
                                                            icone="trash"
                                                            tema="danger"
                                                            confirmacao="Tem certeza que deseja remover essa alternativa? Essa ação é irreversivel."
                                                            onClick={
                                                                () => alternativa.id
                                                                    ? remover(alternativa, () => remove(indice))
                                                                    : remove(indice)
                                                            }
                                                        />
                                                    </div>
                                                )) }
                                            </React.Fragment>
                                        ) }
                                    </Contexto>
                                    <div className="d-flex justify-content-center">
                                        <Botao
                                            icone="plus-lg"
                                            tema="success"
                                            onClick={ () => push({ questao_id: questao?.id, texto: '' }) }
                                        >
                                            Alternativa
                                        </Botao>
                                    </div>
                                </React.Fragment>
                            ) }
                        </FieldArray>
                    </div>
                </div>
            </Form>
        </Modal>
    )
}

export default Editar;
